import React, { forwardRef, useImperativeHandle } from "react";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Typography,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import {
  AccountBalance,
  TrendingUp,
  Add,
  Remove,
  Edit,
  Create,
  Close,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { GET_WALLET_ACTIVITIES } from "../graphql/wallet-history";
import { WalletActivity, ActivityType } from "../types/wallet-history";

interface WalletActivitiesProps {
  walletId: string;
  limit?: number;
}

const getActivityIcon = (type: ActivityType) => {
  switch (type) {
    case ActivityType.WALLET_CREATED:
      return <Create />;
    case ActivityType.TRADE_OPENED:
      return <TrendingUp />;
    case ActivityType.POSITION_OPENED:
      return <Add />;
    case ActivityType.POSITION_CLOSED:
      return <Close />;
    case ActivityType.BALANCE_CHANGED:
      return <AccountBalance />;
    case ActivityType.WALLET_UPDATED:
      return <Edit />;
    case ActivityType.DEPOSIT:
      return <Add />;
    case ActivityType.WITHDRAWAL:
      return <Remove />;
    default:
      return <AccountBalance />;
  }
};

const getActivityColor = (type: ActivityType) => {
  switch (type) {
    case ActivityType.WALLET_CREATED:
    case ActivityType.TRADE_OPENED:
    case ActivityType.POSITION_OPENED:
    case ActivityType.DEPOSIT:
      return "success";
    case ActivityType.WITHDRAWAL:
    case ActivityType.POSITION_CLOSED:
      return "error";
    case ActivityType.BALANCE_CHANGED:
    case ActivityType.WALLET_UPDATED:
      return "info";
    default:
      return "default";
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatDate = (timestamp: string) => {
  return new Date(timestamp).toLocaleString();
};

export const WalletActivitiesComponent = forwardRef<any, WalletActivitiesProps>(
  ({ walletId, limit = 50 }, ref) => {
    const { loading, error, data, refetch } = useQuery(GET_WALLET_ACTIVITIES, {
      variables: { walletId, limit },
      fetchPolicy: "cache-and-network",
    });

    useImperativeHandle(ref, () => ({
      refetch,
    }));

    if (loading) {
      return (
        <Box p={2}>
          <Typography>Loading wallet activities...</Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box p={2}>
          <Typography color="error">
            Error loading wallet activities: {error.message}
          </Typography>
        </Box>
      );
    }

    const activities = data?.walletActivities || [];

    if (activities.length === 0) {
      return (
        <Box p={2}>
          <Typography>No activities available for this wallet.</Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Wallet Activities
        </Typography>
        <Paper>
          <List>
            {activities.map((activity: WalletActivity, index: number) => (
              <React.Fragment key={activity.id}>
                <ListItem>
                  <ListItemIcon>
                    <Box
                      sx={{
                        color: `${getActivityColor(activity.type)}.main`,
                      }}
                    >
                      {getActivityIcon(activity.type)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box
                        component="span"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Typography variant="subtitle1" component="span">
                          {activity.title}
                        </Typography>
                        <Chip
                          label={activity.type}
                          color={getActivityColor(activity.type) as any}
                          size="small"
                        />
                        {activity.amount && (
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              color:
                                activity.amount >= 0
                                  ? "success.main"
                                  : "error.main",
                              fontWeight: "bold",
                            }}
                          >
                            {activity.amount >= 0 ? "+" : ""}
                            {formatCurrency(activity.amount)}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box component="span">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="span"
                        >
                          {formatDate(activity.timestamp)}
                        </Typography>
                        {activity.description && (
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5 }}
                            component="span"
                          >
                            {activity.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < activities.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </Box>
    );
  },
);
